import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SubjectSlug } from '@jeraisonne/db';
import type { AppStudentObjective, AppObjectiveTemplate } from './objective-types';

type RawObjective = {
  id: string;
  student_id: string;
  is_active: boolean;
  selected_subject: string | null;
  selected_chapter_id: string | null;
  selected_pack_id: string | null;
  custom_filters: Record<string, unknown>;
  deadline: string | null;
  created_at: string;
  updated_at: string;
  objective_templates: {
    id: string;
    title: string;
    description: string;
    type: string;
    allowed_levels: string[];
    subject: string | null;
    filters: Record<string, unknown>;
    requires_pack_selection: boolean;
    content_type: string;
    is_active: boolean;
    display_order: number;
    illustration_url: string | null;
    default_deadline: string | null;
  } | null;
};

/**
 * `selected_pack_id` / `requires_pack_selection` (migration 0514) sont postérieurs
 * aux types générés → client générique pour ces lectures / écritures.
 */
function untyped(supabase: SupabaseClient<Database>): SupabaseClient {
  return supabase as unknown as SupabaseClient;
}

function mapRaw(raw: RawObjective): AppStudentObjective | null {
  const t = raw.objective_templates;
  if (!t) return null;

  const template: AppObjectiveTemplate = {
    id: t.id,
    title: t.title,
    description: t.description,
    type: t.type,
    allowedLevels: t.allowed_levels as AppObjectiveTemplate['allowedLevels'],
    subject: (t.subject ?? null) as AppObjectiveTemplate['subject'],
    filters: t.filters,
    requiresPackSelection: t.requires_pack_selection ?? false,
    contentType: (t.content_type ?? 'exercises') as 'exercises' | 'epreuves',
    isActive: t.is_active,
    displayOrder: t.display_order,
    illustrationUrl: t.illustration_url ?? null,
    defaultDeadline: t.default_deadline ?? null,
  };

  return {
    id: raw.id,
    studentId: raw.student_id,
    template,
    selectedSubject: (raw.selected_subject ?? null) as SubjectSlug | null,
    selectedChapterId: raw.selected_chapter_id ?? null,
    selectedPackId: raw.selected_pack_id ?? null,
    customFilters: raw.custom_filters,
    isActive: raw.is_active,
    deadline: raw.deadline ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export async function getActiveObjectives(
  supabase: SupabaseClient<Database>,
  studentId: string,
): Promise<AppStudentObjective[]> {
  const { data, error } = await untyped(supabase)
    .from('student_objectives')
    .select(
      `
      id, student_id, is_active, selected_subject, selected_chapter_id, selected_pack_id,
      custom_filters, deadline, created_at, updated_at,
      objective_templates (
        id, title, description, type, allowed_levels, subject,
        filters, requires_pack_selection, content_type, is_active, display_order,
        illustration_url, default_deadline
      )
    `,
    )
    .eq('student_id', studentId)
    .eq('is_active', true)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data ?? [])
    .map((r) => mapRaw(r as unknown as RawObjective))
    .filter((o): o is AppStudentObjective => o !== null);
}

export async function createObjective(
  supabase: SupabaseClient<Database>,
  studentId: string,
  params: {
    objectiveTemplateId: string;
    selectedSubject?: SubjectSlug | null;
    selectedPackId?: string | null;
    customFilters?: Record<string, unknown>;
    deadline?: string | null;
  },
) {
  const { data, error } = await untyped(supabase)
    .from('student_objectives')
    .insert({
      student_id: studentId,
      objective_template_id: params.objectiveTemplateId,
      selected_subject: params.selectedSubject ?? null,
      selected_pack_id: params.selectedPackId ?? null,
      custom_filters: params.customFilters ?? {},
      deadline: params.deadline ?? null,
      is_active: true,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateObjective(
  supabase: SupabaseClient<Database>,
  objectiveId: string,
  params: {
    selectedSubject?: SubjectSlug | null;
    selectedPackId?: string | null;
    customFilters?: Record<string, unknown>;
    deadline?: string | null;
  },
) {
  const patch: {
    selected_subject?: SubjectSlug | null;
    selected_pack_id?: string | null;
    custom_filters?: Record<string, unknown>;
    deadline?: string | null;
  } = {};
  if ('selectedSubject' in params) patch.selected_subject = params.selectedSubject ?? null;
  if ('selectedPackId' in params) patch.selected_pack_id = params.selectedPackId ?? null;
  if ('customFilters' in params && params.customFilters)
    patch.custom_filters = params.customFilters;
  if ('deadline' in params) patch.deadline = params.deadline ?? null;

  const { error } = await untyped(supabase)
    .from('student_objectives')
    .update(patch)
    .eq('id', objectiveId);

  if (error) throw error;
}

export async function deactivateObjective(supabase: SupabaseClient<Database>, objectiveId: string) {
  const { error } = await supabase
    .from('student_objectives')
    .update({ is_active: false })
    .eq('id', objectiveId);

  if (error) throw error;
}
